import { Link, useLocation } from "@tanstack/react-router";
import {
  BarChart3,
  Building2,
  CalendarRange,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  Layers,
  Plug,
  Radio,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Users,
  X,
} from "lucide-react";
import foxLogo from "@/assets/nymbl-fox-blue.png";
import nymblLogoWhite from "@/assets/nymbl-logo-white.png";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";
import { UserMenu } from "./UserMenu";

interface NavItem {
  label: string;
  icon: typeof Layers;
  href: string;
  disabled?: boolean;
  badge?: number;
  badgeTone?: "danger" | "warn" | "info";
  /**
   * Hidden entirely (not just disabled) for roles without the `viewSignals`
   * capability (SA/ACCT/FIN) — CEO and RM only, per §5 permission matrix.
   */
  ceoOrRmOnly?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: "Manage",
    items: [
      { label: "Solutions", icon: Layers, href: "/dashboard" },
      { label: "Staffing Requests", icon: Inbox, href: "/dashboard/staffing-requests" },
      { label: "Signals", icon: Radio, href: "/dashboard/signals", ceoOrRmOnly: true },
      { label: "Resources", icon: Users, href: "/dashboard/resources" },
      { label: "Schedule", icon: CalendarRange, href: "/dashboard/schedule" },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Accounts", icon: Building2, href: "/dashboard/accounts" },
      { label: "Reporting", icon: BarChart3, href: "/dashboard/reporting" },
    ],
  },
  {
    label: "System",
    items: [
      {
        label: "Configuration",
        icon: SettingsIcon,
        href: "/dashboard/settings/users",
      },
      { label: "Logs", icon: ScrollText, href: "/dashboard/logs" },
      {
        label: "Integrations",
        icon: Plug,
        href: "/dashboard/integrations",
      },
    ],
  },
];

function NavRow({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  onClick: () => void;
}) {
  const location = useLocation();
  const isActive =
    item.href === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname.startsWith(item.href);

  const content = (
    <>
      <span className={cn("flex flex-1 items-center gap-2.5", collapsed && "flex-none")}>
        <item.icon className="h-4 w-4 shrink-0 opacity-80" />
        {!collapsed && <span>{item.label}</span>}
      </span>
      {!collapsed && item.badge != null && item.badge > 0 ? (
        <span
          className={cn(
            "rounded-full px-1.5 py-px text-[10px] font-semibold leading-4",
            item.badgeTone === "danger" && "bg-[var(--fit-at-risk)] text-white",
            item.badgeTone === "warn" && "bg-warning text-warning-950",
            item.badgeTone === "info" && "bg-white/15 text-white",
            !item.badgeTone && "bg-white/15 text-white",
          )}
        >
          {item.badge}
        </span>
      ) : null}
      {!collapsed && item.disabled && (
        <span className="text-[9px] uppercase tracking-wider text-white/40">soon</span>
      )}
    </>
  );

  const baseClasses = cn(
    "flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-[12px] font-medium transition-colors",
    collapsed && "justify-center px-0",
    isActive
      ? "bg-white/12 text-white shadow-[inset_2px_0_0_var(--sidebar-primary)]"
      : "text-white/70 hover:bg-white/8 hover:text-white",
    item.disabled && "pointer-events-none opacity-40",
  );

  if (item.disabled) {
    return (
      <div className={baseClasses} title={collapsed ? item.label : undefined}>
        {content}
      </div>
    );
  }
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={baseClasses}
      title={collapsed ? item.label : undefined}
    >
      {content}
    </Link>
  );
}

export function Sidebar() {
  const { isMobile, mobileOpen, setMobileOpen, collapsed, toggleCollapsed } = useSidebar();
  const { data: session } = useSession();
  const role = session?.user?.role;
  const isAdmin = role === "admin" || role === "CEO" || role === "RM";
  const isCeoOrRm = role === "CEO" || role === "RM";

  const sections = SECTIONS.map((s) => {
    if (s.label === "System") {
      return { ...s, items: s.items.map((i) => ({ ...i, disabled: !isAdmin })) };
    }
    return { ...s, items: s.items.filter((i) => !i.ceoOrRmOnly || isCeoOrRm) };
  });

  const close = () => isMobile && setMobileOpen(false);
  const isCollapsed = !isMobile && collapsed;

  const content = (
    <aside
      className={cn(
        "flex flex-col bg-sidebar text-sidebar-foreground",
        isMobile ? "h-full w-56" : "h-full",
      )}
      style={
        isMobile
          ? undefined
          : { width: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)" }
      }
    >
      <div
        className="flex shrink-0 items-center gap-2.5 border-b border-sidebar-border px-3 font-display"
        style={{ height: "var(--topbar-h)" }}
      >
        {isCollapsed ? (
          <img
            src={foxLogo}
            alt="Nymbl"
            className="mx-auto h-5 w-5"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        ) : (
          <>
            <img src={nymblLogoWhite} alt="Nymbl" className="h-6 w-auto" />
            <span className="h-4 w-px bg-white/[0.32]" />
            <span className="text-[11px] font-medium tracking-[0.2em] text-white/[0.82] uppercase">
              Staff
            </span>
          </>
        )}
        {isMobile && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="ml-auto rounded p-1.5 text-white/70 hover:bg-white/10"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!isCollapsed && (
        <div className="px-2 pb-1 pt-2.5">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <input
              type="search"
              placeholder="Search Solutions, Resources, Accounts…"
              className="h-7 w-full rounded-md border border-white/10 bg-white/10 pl-7 pr-3 text-[12px] text-white placeholder-white/50 outline-none transition focus:border-white/30 focus:bg-white/15"
            />
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section, idx) => (
          <div key={section.label} className={cn(idx > 0 && "mt-4")}>
            {!isCollapsed && (
              <div className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavRow key={item.label} item={item} collapsed={isCollapsed} onClick={close} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-sidebar-border px-2 py-2">
        <UserMenu collapsed={isCollapsed} />
        {!isCollapsed && (
          <div className="mt-1.5 flex items-center justify-between px-1 text-[10px] text-white/40">
            <span>Phase 1.2 · Foundations</span>
            <span>Production · v3.0</span>
          </div>
        )}
        {!isMobile && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            className={cn(
              "mt-1.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-[10px] text-white/40 hover:bg-white/10 hover:text-white/70",
              isCollapsed ? "justify-center" : "justify-start",
            )}
          >
            {collapsed ? (
              <ChevronsRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronsLeft className="h-3.5 w-3.5" />
            )}
            {!isCollapsed && <span>Collapse</span>}
          </button>
        )}
      </div>
    </aside>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <>
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
        <div className="fixed left-0 top-0 z-50 h-screen">{content}</div>
      </>
    );
  }
  return content;
}
